import { Box, Button, Card, Group, Paper, SegmentedControl, Stack, Text } from "@mantine/core";
import CodeMirror, { EditorView } from "@uiw/react-codemirror";
import { useCallback, useEffect, useRef, useState } from "react";
import { EDITOR_BACKGROUND, EDITOR_STYLE } from "../../common/editor-theme";
import { ABANDONED, BLANK_ENTRY, type Entry, NO_SESSION, Repl, type Session, written } from "../../common/repl-console";
import { type Chunk, DRAW_DELAY, DROPPED_NOTE, IDLE, isWorking, MAX_OUTPUT, message, NOTHING_HELD, type Run, runStats, RunStatus } from "../../common/run-output";
import { useInitialHashState, useRegisterShareState } from "../../common/share-state";
import { Panes } from "../../common/split-panes";
import { UtilityTitle } from "../../common/utility-title";
import { type Scope, Variables } from "../../common/variables-panel";
import { IconPlayerPlay, IconPlayerStop } from "../../icons";
import { EDITOR_EXTENSIONS } from "./editor";
import type { Language } from "./engine";
import { COULD_NOT_START, FUNCTIONS, LANGUAGE_NAMES, MARKS, type Mode, OUT_OF_MEMORY, OUT_OF_MEMORY_NOTE, runMessage, sessionMessage, STOPPED_NOTE } from "./messages";
import { SAMPLES } from "./samples";
import { unfinished } from "./syntax";
import type { Message, Request } from "./worker";

export default function JavaScript() {
  const initialState = useInitialHashState<{
    mode?: string;
    language?: string;
    code?: string;
    line?: string;
  }>();

  const [mode, setMode] = useState<Mode>(initialState?.mode === "repl" ? "repl" : "script");
  const opened: Language = initialState?.language === "typescript" ? "typescript" : "javascript";
  const [language, setLanguage] = useState<Language>(opened);

  // Uncontrolled, as in /json: feeding the text back in would replace the document and send the caret to the end.
  // The ref is what a mode switch reads the document back from, since the editor is unmounted while REPL is showing.
  const codeRef = useRef(initialState?.code ?? SAMPLES[opened]);

  const [output, setOutput] = useState<string | null>(null);
  const [dropped, setDropped] = useState(false);
  const [run, setRun] = useState<Run>(IDLE);

  // What the last run left bound, read once as it ended: null until a run has ended with something to say.
  const [scope, setScope] = useState<Scope | null>(null);

  // The REPL's transcript, what its session holds now, and the line at the prompt that has not been entered yet.
  const [session, setSession] = useState<Session>(NO_SESSION);
  const [sessionScope, setSessionScope] = useState<Scope | null>(null);
  const [line, setLine] = useState(initialState?.line ?? "");

  // The worker is made on the first Run: the engine behind it is half a megabyte of WebAssembly nobody passing through
  // the page asked for, and it is only the first request that pays to compile it.
  const workerRef = useRef<Worker | null>(null);
  const waiting = useRef(new Map<number, (message: Message) => void>());
  const runId = useRef(0);

  // What has arrived from the worker but not yet been drawn, the timer that will draw it, and where it belongs.
  const held = useRef<Chunk>(NOTHING_HELD);
  const drawing = useRef<ReturnType<typeof setTimeout> | null>(null);
  const shown = useRef("");
  const sink = useRef<Mode>("script");

  const syncShareState = useRegisterShareState(() => ({
    mode,
    // The language is on screen in both modes and decides what either one runs, so it is on the link in both.
    language,
    code: mode === "script" ? codeRef.current : undefined,
    // The line at the prompt is the REPL's script; the transcript above it is what an engine did here, and stays.
    line: mode === "repl" && line ? line : undefined,
  }));

  // `window.javascriptEditor` is how devtools and the Playwright spec read the script; drop it on the way out.
  useEffect(() => () => {
    self.javascriptEditor = undefined;
  }, []);

  const stopWorker = useCallback(() => {
    // Nothing else can interrupt a running script: the worker is a thread of its own, and this is how it ends.
    workerRef.current?.terminate();
    workerRef.current = null;
    waiting.current.clear();
  }, []);

  const draw = useCallback(() => {
    if (drawing.current !== null) clearTimeout(drawing.current);
    drawing.current = null;

    const { text, dropped } = held.current;
    if (!text && !dropped) return;
    held.current = NOTHING_HELD;

    // What the prompt is running belongs to the block that is running, which is what puts it under the line itself.
    if (sink.current === "repl") {
      setSession((session) =>
        session.current ? { ...session, current: written(session.current, text, dropped) } : session
      );
      return;
    }

    // The tail of a run that never stops printing, since a scrollback nothing can scroll past is not worth keeping.
    const next = shown.current + text;
    shown.current = next.length > MAX_OUTPUT ? next.slice(-MAX_OUTPUT) : next;
    setOutput(shown.current);
    if (dropped || next.length > MAX_OUTPUT) setDropped(true);
  }, []);

  // Held rather than drawn on arrival: a script printing in a loop posts far faster than a page can usefully redraw.
  const receive = useCallback((chunk: Chunk) => {
    held.current = { text: held.current.text + chunk.text, dropped: held.current.dropped || chunk.dropped };
    if (drawing.current === null) drawing.current = setTimeout(draw, DRAW_DELAY);
  }, [draw]);

  useEffect(() => () => {
    stopWorker();
    if (drawing.current !== null) clearTimeout(drawing.current);
  }, [stopWorker]);

  // A module worker, so the engine and the WebAssembly behind it are a chunk of their own that the page never loads.
  // The URL is spelled statically because that is the whole of what lets the bundler find the worker and emit it.
  const makeWorker = useCallback(() => {
    const worker = new Worker(new URL("/assets/worker-gagv5mdn.js", import.meta.url), { type: "module" });

    worker.onmessage = ({ data }: MessageEvent<Message>) => {
      if (data.kind === "output") return receive({ text: data.text, dropped: data.dropped });
      // The engine is up, and what the page has been calling starting is now running.
      if (data.kind === "started") return setRun({ state: "running" });

      const settle = waiting.current.get(data.id);
      waiting.current.delete(data.id);
      settle?.(data);
    };

    // A worker that cannot be built answers nothing at all, so whatever is waiting is told rather than left waiting.
    worker.onerror = (event) => {
      const failed = event.message || COULD_NOT_START;
      for (const [id, settle] of waiting.current) settle({ kind: "done", id, failed });
      waiting.current.clear();
    };

    return worker;
  }, [receive]);

  // One request, answered once: the worker runs one thing at a time because the page only ever asks for one.
  const ask = useCallback((request: Request) => {
    const worker = workerRef.current ?? (workerRef.current = makeWorker());

    return new Promise<Message>((settle) => {
      waiting.current.set(request.id, settle);
      worker.postMessage(request);
    });
  }, [makeWorker]);

  const handleChange = useCallback((next: string) => {
    codeRef.current = next;
    syncShareState();
  }, [syncShareState]);

  // The script in the editor is somebody's own unless it is still the one this page put there, and a sample is only
  // ever a sample of the language it was written for — the same rule /jwt applies to a key it generated itself.
  const handleLanguage = useCallback((next: Language) => {
    if (codeRef.current === SAMPLES[language]) codeRef.current = SAMPLES[next];
    setLanguage(next);
  }, [language]);

  // The names a session bound live in the engine, so whatever takes that away is the end of the session.
  const endSession = useCallback((note: string) => {
    setSessionScope(null);
    setSession((session) => {
      if (!session.current && session.entries.length === 0) return session;
      const closed = session.current ?? BLANK_ENTRY;
      return { entries: [...session.entries, { ...closed, note }], current: null };
    });
  }, []);

  const handleStop = useCallback(() => {
    runId.current++;
    stopWorker();
    // Stopping takes the engine away, not what it had already printed.
    draw();
    setRun({ state: "stopped" });
    endSession(STOPPED_NOTE);
  }, [draw, endSession, stopWorker]);

  // A heap run out is not given back, so the engine goes the way a stopped one does and the next line starts another.
  const handleExhausted = useCallback(() => {
    stopWorker();
    setRun({ state: "failed", message: OUT_OF_MEMORY });
    endSession(OUT_OF_MEMORY_NOTE);
  }, [endSession, stopWorker]);

  const handleRun = useCallback(async () => {
    const id = ++runId.current;
    // A worker that outlives the run keeps the engine warm, so only the first script pays for compiling one.
    const booting = workerRef.current === null;
    setRun({ state: booting ? "starting" : "running" });

    sink.current = "script";
    held.current = NOTHING_HELD;
    shown.current = "";
    setOutput("");
    setDropped(false);
    // The names on screen were the last run's, and this one starts on nothing: they go the way the output goes.
    setScope(null);

    const answer = await ask({ id, kind: "run", code: codeRef.current, language });
    if (runId.current !== id || answer.kind !== "done") return;

    // The worker posted the last of the output before returning, so it is on screen with the time it took.
    draw();
    if (answer.failed !== undefined) {
      // A worker whose engine failed to start is not one to hand the next script to.
      stopWorker();
      setRun({ state: "failed", message: answer.failed });
      return;
    }
    if (answer.exhausted) return handleExhausted();

    setScope(answer.scope ?? null);
    setRun({ state: "finished", seconds: answer.seconds ?? 0 });
  }, [ask, draw, handleExhausted, language, stopWorker]);

  const handleEnter = useCallback(async (text: string) => {
    // The lines join the block at the prompt before anything runs, so the entry is there for its own output to land in.
    const block: Entry = {
      ...(session.current ?? BLANK_ENTRY),
      lines: [...(session.current?.lines ?? []), ...text.split("\n")],
    };
    setSession((session) => ({ ...session, current: block }));
    setLine("");

    // Nothing runs and no engine starts: the block stays where it is, and the prompt under it asks for the rest of it.
    const source = block.lines.join("\n");
    if (unfinished(source)) return;

    const id = ++runId.current;
    const booting = workerRef.current === null;
    sink.current = "repl";
    setRun({ state: booting ? "starting" : "running" });

    const answer = await ask({ id, kind: "enter", code: source, language });
    if (runId.current !== id || answer.kind !== "done") return;

    // The worker posted the last of the output before it returned, so it is under the line that asked for it.
    draw();
    if (answer.failed !== undefined) {
      stopWorker();
      setRun({ state: "failed", message: answer.failed });
      endSession(answer.failed);
      return;
    }
    if (answer.exhausted) return handleExhausted();

    setRun(IDLE);
    setSession((session) => ({ entries: [...session.entries, session.current ?? block], current: null }));
    setSessionScope(answer.scope ?? null);
  }, [ask, draw, endSession, handleExhausted, language, session, stopWorker]);

  // Escape is what a prompt has in place of a way to unsay a line: the block goes, and nothing of it ever ran.
  const handleAbandon = useCallback(() => {
    setLine("");
    setSession((session) =>
      session.current
        ? { entries: [...session.entries, { ...session.current, note: ABANDONED }], current: null }
        : session
    );
  }, []);

  const busy = isWorking(run);
  const spoken = LANGUAGE_NAMES[language];

  return (
    <Stack flex={1} className="fill-screen" gap="md">
      <UtilityTitle
        directory="javascript"
        control={
          <SegmentedControl
            value={mode}
            onChange={(value) => setMode(value as Mode)}
            data={[{ value: "script", label: "Script" }, { value: "repl", label: "REPL" }]}
          />
        }
      >
        JavaScript
      </UtilityTitle>

      <Card withBorder shadow="sm" radius="md">
        <Group align="flex-end" gap="xl" justify="space-between" wrap="nowrap">
          <Group align="center" gap="sm">
            <SegmentedControl
              size="xs"
              value={language}
              onChange={(value) => handleLanguage(value as Language)}
              aria-label="Language"
              data={[{ value: "javascript", label: "JavaScript" }, { value: "typescript", label: "TypeScript" }]}
            />
            {mode === "script" && (
              <Button onClick={handleRun} disabled={busy} leftSection={<IconPlayerPlay size="1rem" />}>
                Run
              </Button>
            )}
            <Button
              variant="default"
              onClick={handleStop}
              disabled={!busy}
              leftSection={<IconPlayerStop size="1rem" />}
            >
              Stop
            </Button>
            {mode === "repl" && <Text size="sm" c="dimmed">Enter runs the line, Shift+Enter adds another.</Text>}
          </Group>
          <RunStatus run={run} starting={`Starting ${spoken}…`} />
        </Group>
      </Card>

      <Panes
        panel={mode === "repl"
          ? (
            <Variables
              busy={busy}
              scope={sessionScope}
              empty={sessionMessage(run, sessionScope, session)}
              section={FUNCTIONS}
            />
          )
          : <Variables busy={busy} scope={scope} empty={runMessage(run, scope)} section={FUNCTIONS} />}
      >
        {mode === "repl"
          ? (
            <Repl
              session={session}
              busy={busy}
              line={line}
              marks={MARKS}
              label={`${spoken} prompt`}
              empty={`${spoken} starts on the first line entered here.`}
              onLine={setLine}
              onEnter={handleEnter}
              onAbandon={handleAbandon}
            />
          )
          : (
            <Paper
              withBorder
              shadow="sm"
              radius="md"
              className="split-main"
              style={{ position: "relative", overflow: "hidden" }}
            >
              <Box style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}>
                <CodeMirror
                  value={codeRef.current}
                  // A percentage height only resolves against a definite one, so the wrapper matches the editor.
                  height="100%"
                  style={EDITOR_STYLE}
                  theme="dark"
                  extensions={EDITOR_EXTENSIONS[language]}
                  onCreateEditor={(view) => {
                    self.javascriptEditor = view;
                  }}
                  onChange={handleChange}
                />
              </Box>
            </Paper>
          )}
      </Panes>

      {/* The REPL has no panel of its own down here: what an entry printed belongs under the line that printed it. */}
      {mode === "script" && (
        <Paper
          withBorder
          shadow="sm"
          radius="md"
          style={{ flex: 1, minHeight: "8rem", overflow: "auto", backgroundColor: EDITOR_BACKGROUND }}
        >
          {/* A log is the live region a screen reader reads out as it fills, which is what this is. */}
          <Text
            component="pre"
            role="log"
            aria-label="Script output"
            ff="monospace"
            fz={EDITOR_STYLE.fontSize}
            p="sm"
            m={0}
            c={output === null ? "dimmed" : undefined}
            style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
          >
            {output === null ? "Nothing has run yet." : dropped ? DROPPED_NOTE + output : output}
          </Text>

          {/* The tally goes after the output rather than into it, so the log stays what the script itself wrote. */}
          {run.state === "finished" && (
            <Text ff="monospace" fz={EDITOR_STYLE.fontSize} c="dimmed" px="sm" pb="sm" mt="xl">
              {runStats(run.seconds, output, dropped)}
            </Text>
          )}
        </Paper>
      )}
    </Stack>
  );
}

declare global {
  var javascriptEditor: EditorView | undefined;
}
